/**
 * Automatic recovery ("heal") for slot-loss booking failures.
 *
 * The Starbase booking endpoint rejects with a structured validation error
 * (preserved on `err.errors` — see bookAppointment / formatAndRethrowError)
 * when the availability snapshot behind a booking is no longer valid:
 *
 *  - `searchExpired`   — the booking tokens from the availability search are
 *                        older than the server's maximum age.
 *  - `providerBooked`  — the provider behind the selected slot was booked out
 *                        from under the customer between search and confirm.
 *
 * Both mean the same thing to the customer — "your search results went stale" —
 * and both used to dead-end as a red error line under the payment method,
 * inviting rage-clicks that failed the same way every time. Instead,
 * attemptBookingHeal re-runs the availability search with the same parameters
 * Step Three uses and:
 *
 *  1. rebooks silently with fresh booking tokens when the exact same start
 *     time is still available (web has no pick-a-pro, so no requested provider
 *     can be lost — any provider at the same time is the same appointment), or
 *  2. returns the customer to the search step (Step Three) with an
 *     explanatory message when the slot is genuinely gone.
 */
import { DateTime } from 'luxon'
import { merge, pick } from 'lodash'
import {
  appointmentAvailabilitySearch,
  refreshDayViewAppointment
} from '../services/GraphQlClientService'
import { trackBookingFailed, trackBookingHeal } from './tracking'

/** Booking failure types (err.errors[].type) that a re-search can recover from. */
const HEALABLE_FAILURE_TYPES = ['searchExpired', 'providerBooked']

/**
 * Message shown on the search step when a heal could not rebook. Rendered by
 * Step Three's `booking-heal-message` banner (auto-dismissed toast-style).
 */
export const BOOKING_HEAL_RETURN_MESSAGE =
  'Sorry — the time you selected is no longer available. Please choose a new time.'

// Mirror Step Three's search window (minutes of variance around the requested
// time, minutes between offered slots) so the heal search sees exactly the
// results the customer would see back on the search step.
const SEARCH_TIME_VARIANCE_MINUTES = 8 * 60
const SEARCH_TIME_INTERVAL_MINUTES = 30

/**
 * Extracts the healable failure type from a booking error, if present.
 *
 * @param {*} err - the error thrown by bookAppointment.
 * @returns {'searchExpired'|'providerBooked'|null}
 */
export const parseHealableBookingError = err => {
  const item =
    err && Array.isArray(err.errors)
      ? err.errors.find(e => e && HEALABLE_FAILURE_TYPES.includes(e.type))
      : undefined
  return item ? item.type : null
}

/**
 * Overlays a fresh availability result onto an already-built booking payload.
 *
 * Only the token-bearing fields are taken from the fresh result: the classic
 * booking token, the appointment services (fresh per-service bookingToken +
 * providerId), and the start times (equal by definition of an exact match).
 * Everything else — notes, tip fields, payment ids, and especially the
 * computed price fields (`finalPriceWithTaxAndTip` etc., which a raw search
 * result carries as null) — is preserved so the amount the customer confirmed
 * cannot change mid-heal.
 *
 * @param {object} appointment - the booking payload that just failed.
 * @param {object} freshResult - an availability result at the same actualStartDateTime.
 * @returns {object} a rebookable payload with fresh booking tokens.
 */
export const mergeFreshAvailability = (appointment, freshResult) => ({
  ...appointment,
  token: freshResult.token,
  bookingTokens: freshResult.bookingTokens,
  appointmentServices: freshResult.appointmentServices,
  startDateTime: freshResult.startDateTime,
  actualStartDateTime: freshResult.actualStartDateTime,
})

/**
 * Attempts to heal a failed booking. Call from a booking path's catch block.
 *
 * Non-healable errors are rethrown untouched so the caller's normal error
 * handling (footer error line, Booking Failed tracking) still applies. For a
 * healable error this either resolves to the booked appointment (silent
 * rebook succeeded) or resolves to null after dispatching the customer back
 * to the search step — in which case the caller must NOT finalize the booking.
 *
 * At most one heal per submit: if the rebook itself fails with another
 * healable error the customer goes back to search rather than looping; a
 * non-healable rebook failure (e.g. a card decline) is rethrown.
 *
 * @param {*} err - the error thrown by the booking attempt.
 * @param {object} ctx
 * @param {Function} ctx.dispatch - booking-flow dispatch.
 * @param {object} ctx.appointment - the selected appointment (state.selectedAppointment).
 * @param {object} ctx.address - the booking address (state.address).
 * @param {object[]} ctx.serviceRequests - the requested services (state.serviceRequests).
 * @param {string} ctx.timezone - the market timezone.
 * @param {object[]} ctx.marketServices - market services for the selected market.
 * @param {object} ctx.market - at least { shortName }, for tracking.
 * @param {string|null} ctx.selectedPaymentMethodId - raw selection (PLATFORM_PAY sentinel for wallets).
 * @param {(freshResult: object) => Promise<object>} ctx.rebook - books the fresh
 *   availability result on the caller's payment path and resolves to the booked appointment.
 * @returns {Promise<object|null>} the booked appointment, or null when the
 *   customer was returned to the search step.
 */
export const attemptBookingHeal = async (
  err,
  {
    dispatch,
    appointment,
    address,
    serviceRequests,
    timezone,
    marketServices,
    market,
    selectedPaymentMethodId,
    rebook,
  }
) => {
  const failureType = parseHealableBookingError(err)
  if (!failureType) {
    throw err
  }

  const healDetails = { failureType, appointment, market, selectedPaymentMethodId }

  const returnToSearch = () => {
    // the customer's attempt still ended without a booking, so keep the
    // Booking Failed funnel honest before recording the heal outcome
    trackBookingFailed(failureType, err.message, appointment, market, selectedPaymentMethodId)
    trackBookingHeal('returned_to_search', healDetails)
    dispatch({
      step: 3,
      selectedAppointment: false,
      bookingHealMessage: BOOKING_HEAL_RETURN_MESSAGE,
    })
    return null
  }

  let freshResult
  try {
    if (appointment.bookingTokens?.length) {
      freshResult = await refreshDayViewAppointment(appointment, address, timezone)
    } else {
      const search = await appointmentAvailabilitySearch(
        // suppress the service's dispatch: it clears selectedAppointment as its
        // first act, which would crash Step Five while it is still mounted. The
        // heal dispatches its own state changes once the outcome is known.
        () => null,
        DateTime.fromSeconds(appointment.actualStartDateTime).setZone(timezone).toJSDate(),
        timezone,
        SEARCH_TIME_VARIANCE_MINUTES,
        SEARCH_TIME_INTERVAL_MINUTES,
        pick(address, ['street', 'city', 'state', 'zip', 'type']),
        serviceRequests.map(sr => merge({}, { backToBack: false, instances: 1 }, sr)),
        null,
        marketServices
      )
      freshResult = (search?.data || []).find(
        a => a.actualStartDateTime === appointment.actualStartDateTime
      )
    }
  } catch {
    // the heal search itself failed — nothing to verify against, so let the
    // customer search again themselves
    return returnToSearch()
  }

  if (!freshResult) {
    return returnToSearch()
  }

  try {
    const bookedAppointment = await rebook(freshResult)
    trackBookingHeal('rebooked', healDetails)
    return bookedAppointment
  } catch (rebookErr) {
    if (parseHealableBookingError(rebookErr)) {
      return returnToSearch()
    }
    throw rebookErr
  }
}
